import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import icon_info from '../../assets/icons/information-dark.svg';
import icon_info_line from '../../assets/icons/info_icon.svg';
import icon_result from '../../assets/icons/high-five-hand.svg';
import icon_time from '../../assets/icons/result_time.svg';
import icon_time_blue from '../../assets/icons/result_time_blue.svg';
import icon_avg_time from '../../assets/icons/avg_time.svg';
import icon_punteggio from '../../assets/icons/total_score.svg';
import icon_correct_answer from '../../assets/icons/answer_correct.svg';
import icon_wrong_answer from '../../assets/icons/answer_wrong.svg';
import icon_missing_answer from '../../assets/icons/answer_null.svg';
import icon_not_counted from '../../assets/icons/not_counted.svg';
import icon_bulb from '../../assets/icons/bulb.svg';
import icon_degree from '../../assets/icons/degree.svg';
import icon_pass from '../../assets/icons/pass_test.svg';
import icon_not_pass from '../../assets/icons/not_pass_test.svg';
import icon_correct_not_shure from '../../assets/icons/correct_uncertain.svg';
import icon_wrong_not_shure from '../../assets/icons/uncertain_wrong.svg';
import close_icon from '../../assets/icons/close.svg';
import icon_check_certified from '../../assets/icons/check-certified.svg';

import DetailedResultsModal from '../../_components/Modals/DetailedResultsModal';
import DetailedPassModal from '../../_components/Modals/DetailedPassModal';
import TresholdProgress from '../../_components/Result/TresholdProgress';

import { useTranslation } from 'react-i18next';
import { prefixedT } from '../../i18n/utils';

export default function ResultSummary({
  simulationData,
  handleOpenCloseResultModal,
  setStep,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [showDisciplineModal, setShowDisciplineModal] = useState(false);

  const thresholds = simulationData?.navigation?.structure?.thresholds || [];

  const isModuleBasedTresholds =
    thresholds.length > 0 &&
    thresholds.every(
      (threshold) => typeof threshold.module_number === 'number'
    );

  function hasPassedTreshold() {
    if (isModuleBasedTresholds) {
      return (
        simulationData?.navigation?.modules?.every((module) => {
          const moduleThreshold = thresholds.find(
            (threshold) =>
              threshold.module_number === module.module_number &&
              threshold.is_passing === true
          );

          const moduleStats =
            simulationData?.stats_per_module?.[module.module_number];

          return (
            !moduleThreshold ||
            (moduleStats?.correct_score || 0) >= (moduleThreshold.score || 0)
          );
        }) ?? false
      );
    }

    const passingThreshold = thresholds.find(
      (threshold) => threshold.is_passing === true
    );
    return passingThreshold
      ? (simulationData?.stats?.correct_score || 0) >=
          (passingThreshold.score || 0)
      : false;
  }

  const universityLogos =
    simulationData?.admission_test?.sponsors?.[0]?.universities || [];

  return (
    <>
      {/* Close button */}
      {(simulationData?.type === 'incorrect_questions' ||
        isModuleBasedTresholds) &&
        (isModuleBasedTresholds ? (
          <img
            src={close_icon}
            onClick={handleOpenCloseResultModal}
            className='pointer close-button-absolute'
            alt='Close results modal'
          />
        ) : (
          <div className='d-flex justify-content-end'>
            <img
              src={close_icon}
              onClick={handleOpenCloseResultModal}
              className='pointer'
              alt='Close results modal'
            />
          </div>
        ))}

      {/* Passed or Not passed Title with Icon */}
      {!!thresholds.length ? (
        <>
          {hasPassedTreshold() ? (
            <>
              <img
                src={icon_pass}
                className='mx-auto section-icon'
                alt='Test passed'
              />
              <h1 className='text-center mt-2'>
                {t(prefixedT('RESULT_SCREEN.SIMULATION_PASSED'))}
              </h1>
            </>
          ) : (
            <>
              <img
                src={icon_not_pass}
                className='mx-auto section-icon'
                alt='Test not passed'
              />
              <h1 className='text-center mt-2'>
                {t(prefixedT('RESULT_SCREEN.SIMULATION_NOT_PASSED'))}
              </h1>
            </>
          )}

          {isModuleBasedTresholds && (
            <>
              <div className='mt-2 universities-logos-container'>
                {/* Display first 3 university logos */}
                {universityLogos?.slice(0, 3).map((university, index) => (
                  <img
                    key={university.university_id || index}
                    src={university.university_image_url}
                    alt={university.university_name}
                    className='university-logo'
                  />
                ))}

                {/* Show remaining count if more than 3 universities */}
                {universityLogos?.length > 3 && (
                  <>
                    <span className='universities-count'>
                      + {Math.abs(universityLogos.length - 3)}{' '}
                      {t(prefixedT('RESULT_SCREEN.UNIVERSITIES'))}
                    </span>

                    {/* Check certified icon */}
                    <img
                      src={icon_check_certified}
                      alt='Certified'
                      width={20}
                      height={20}
                      className='certified-icon'
                    />
                  </>
                )}
              </div>

              <div className='result_label mt-3'>
                <img src={icon_time_blue} alt='Time taken' />
                <span>
                  {new Date(
                    (simulationData?.consumed_time || 0) * 1000
                  ).getMinutes()}{' '}
                  min{' '}
                  {new Date(
                    (simulationData?.consumed_time || 0) * 1000
                  ).getSeconds()}{' '}
                  sec
                </span>
              </div>

              <span className='treshold_result big_result mt-3'>
                {Math.round(
                  (simulationData?.stats?.total_score + Number.EPSILON) * 100
                ) / 100}
                <img
                  src={icon_correct_answer}
                  width={20}
                  height={20}
                  alt='Score indicator'
                />
                <span className='small_bold_result'>
                  {t(prefixedT('RESULT_SCREEN.OUT_OF'))}
                  {` ${simulationData?.stats?.max_score}`}
                </span>
              </span>
            </>
          )}
        </>
      ) : (
        <>
          <img
            src={icon_result}
            className='mx-auto section-icon'
            alt='Simulation completed'
          />

          {simulationData.type === 'incorrect_questions' ? (
            <h1 className='text-center mt-2'>
              {t(prefixedT('RESULT_SCREEN.REVIEW_COMPLETED'))}
            </h1>
          ) : (
            <h1 className='text-center mt-2'>
              {simulationData?.is_bocconi_design
                ? t(prefixedT('RESULT_SCREEN.PRACTICE_COMPLETED'))
                : t(prefixedT('RESULT_SCREEN.SIMULATION_COMPLETED'))}
            </h1>
          )}
        </>
      )}

      {/* Result cards */}
      <div className='result__cards'>
        {isModuleBasedTresholds ? (
          <>
            {/* Module-based thresholds display for each subject*/}
            {simulationData?.navigation?.modules?.map((module, index) => (
              <div
                key={module?.name || index}
                className='card result-card-relative'
              >
                {/* Info button in top right corner */}
                <button
                  className='info-button-absolute'
                  onClick={() => {
                    setSelectedDiscipline(
                      simulationData?.stats_per_module?.[module?.module_number]
                    );
                    setShowDisciplineModal(true);
                  }}
                >
                  <img src={icon_info_line} width={20} height={20} alt='Info' />
                </button>

                <div className='result_label result-label-column'>
                  <img
                    src={module?.image_url || ''}
                    width={20}
                    height={20}
                    alt={module?.name || 'Module'}
                    className='discipline-icon'
                  />
                  <span>{module?.name || 'Unknown Module'}</span>
                </div>

                <TresholdProgress
                  totalScore={
                    simulationData?.stats_per_module?.[module?.module_number]
                      ?.total_score || 0
                  }
                  maximumScore={
                    simulationData?.stats_per_module?.[module?.module_number]
                      ?.max_score || 0
                  }
                  resultScore={
                    simulationData?.stats_per_module?.[module?.module_number]
                      ?.correct_score || 0
                  }
                  thresholds={thresholds.filter(
                    (t) => t?.module_number === module?.module_number
                  )}
                />
              </div>
            ))}
          </>
        ) : !!thresholds.length ? (
          <>
            <div className='card'>
              <div className='result_label'>
                <img src={icon_info} width={20} height={20} alt='Details' />
                <span>{t(prefixedT('RESULT_SCREEN.DETAILS'))}</span>
              </div>

              <div className='d-flex justify-content-between mt-2'>
                <div className='summary_result'>
                  <img
                    className='mx-auto'
                    src={icon_correct_answer}
                    alt='Correct answers'
                  />
                  <span>{t(prefixedT('RESULT_SCREEN.CORRECT'))}</span>
                  <span>{simulationData?.stats?.correct || 0}</span>
                </div>
                <div className='summary_result ms-4 me-4'>
                  <img
                    className='mx-auto'
                    src={icon_wrong_answer}
                    alt='Wrong answers'
                  />
                  <span>{t(prefixedT('RESULT_SCREEN.WRONG'))}</span>
                  <span>{simulationData?.stats?.wrong || 0}</span>
                </div>
                <div className='summary_result'>
                  <img
                    className='mx-auto'
                    src={icon_missing_answer}
                    alt='Missing answers'
                  />
                  <span>{t(prefixedT('RESULT_SCREEN.MISSING'))}</span>
                  <span>{simulationData?.stats?.missing || 0}</span>
                </div>
                {simulationData?.stats?.not_counted ? (
                  <div className='summary_result ms-4 '>
                    <img
                      className='mx-auto'
                      src={icon_not_counted}
                      alt='Not counted'
                    />
                    <span>{'N.C.'}</span>
                    <span>{simulationData?.stats?.not_counted || 0}</span>
                  </div>
                ) : (
                  ''
                )}
              </div>

              <div className='result_label mt-4'>
                <img src={icon_time_blue} alt='Time taken' />
                <span>
                  {new Date(
                    (simulationData?.consumed_time || 0) * 1000
                  ).getMinutes()}{' '}
                  min{' '}
                  {new Date(
                    (simulationData?.consumed_time || 0) * 1000
                  ).getSeconds()}{' '}
                  sec
                </span>
              </div>
            </div>

            <div className='card'>
              <div className='result_label'>
                <img src={icon_punteggio} alt='Score' />
                <span>{t(prefixedT('RESULT_SCREEN.SCORE'))}</span>
              </div>

              <span className='treshold_result big_result mt-4'>
                {Math.round(
                  (simulationData?.stats?.total_score + Number.EPSILON) * 100
                ) / 100}
                <img
                  src={icon_correct_answer}
                  width={20}
                  height={20}
                  alt='Score indicator'
                />
                <span className='small_bold_result'>
                  {t(prefixedT('RESULT_SCREEN.OUT_OF'))}{' '}
                  {` ${simulationData?.stats?.max_score}`}
                </span>
              </span>

              <TresholdProgress
                maximumScore={simulationData?.stats?.max_score}
                resultScore={simulationData?.stats?.correct_score}
                thresholds={thresholds}
              />
            </div>
          </>
        ) : (
          <>
            <div className='card'>
              {simulationData.type === 'incorrect_questions' ? (
                <div className='result_label_without_margin mt-5'>
                  <span>{t(prefixedT('RESULT_SCREEN.YOU_CORRECTED'))}</span>
                </div>
              ) : (
                <div className='result_label'>
                  <img src={icon_time} alt='Total time' />
                  <span>{t(prefixedT('RESULT_SCREEN.TOTAL_TIME'))}</span>
                </div>
              )}
              {simulationData.type === 'incorrect_questions' ? (
                <>
                  <div className='d-flex justify-content-center align-items-center mt-2'>
                    <span className='big_result me-1 big-result-large'>
                      {(simulationData?.stats?.correct || 0) -
                        (simulationData?.stats?.uncertain_correct || 0)}
                    </span>
                    <img
                      className='icon-min-width'
                      src={icon_correct_answer}
                      alt='Correct answers'
                    />
                  </div>
                  <p className='questions-count-text'>
                    {`${t(prefixedT('RESULT_SCREEN.QUESTIONS_OF'))} ${
                      simulationData?.questions?.length || 0
                    }`}
                  </p>
                </>
              ) : (
                <>
                  <span className='big_result mt-2'>
                    {new Date(
                      (simulationData?.consumed_time || 0) * 1000
                    ).getMinutes()}
                    m{' '}
                    {new Date(
                      (simulationData?.consumed_time || 0) * 1000
                    ).getSeconds()}
                    s
                  </span>
                  <div className='result_label_small mt-4'>
                    <img src={icon_avg_time} alt='Average time' />
                    <span>
                      {t(prefixedT('RESULT_SCREEN.AVG_TIME_FOR_QUESTION'))}
                    </span>
                  </div>
                  <span className='small_result mt-2'>
                    {(() => {
                      const consumedTime = simulationData?.consumed_time || 0;
                      const questionsLength =
                        simulationData?.questions?.length || 1; // Prevent division by zero
                      const avgTime = consumedTime / questionsLength;

                      if (avgTime > 60) {
                        return (
                          Math.round(avgTime / 60) +
                          'm ' +
                          Math.round(avgTime % 60) +
                          's'
                        );
                      } else {
                        return Math.round(avgTime * 10) / 10 + 's';
                      }
                    })()}
                  </span>
                </>
              )}
            </div>

            <div className='card'>
              {simulationData.type !== 'incorrect_questions' && (
                <div className='result_label'>
                  <img src={icon_punteggio} alt='Score' />
                  <span>{t(prefixedT('RESULT_SCREEN.SCORE'))}</span>
                </div>
              )}
              {simulationData.type !== 'incorrect_questions' && (
                <span className='big_result mt-2'>
                  {Math.round(
                    (simulationData?.stats?.total_score + Number.EPSILON) * 100
                  ) / 100}{' '}
                  <span className='total-score-text'>
                    {`/ ${simulationData?.stats?.max_score}`}
                  </span>
                </span>
              )}
              {simulationData.type !== 'incorrect_questions' ? (
                <div className='d-flex justify-content-between'>
                  <div className='summary_result'>
                    <img
                      className='mx-auto'
                      src={icon_correct_answer}
                      alt='Correct answers'
                    />
                    <span>{t(prefixedT('RESULT_SCREEN.CORRECT'))}</span>
                    <span>{simulationData?.stats?.correct || 0}</span>
                  </div>
                  <div className='summary_result ms-4 me-4'>
                    <img
                      className='mx-auto'
                      src={icon_wrong_answer}
                      alt='Wrong answers'
                    />
                    <span>{t(prefixedT('RESULT_SCREEN.WRONG'))}</span>
                    <span>{simulationData?.stats?.wrong || 0}</span>
                  </div>
                  <div className='summary_result'>
                    <img
                      className='mx-auto'
                      src={icon_missing_answer}
                      alt='Missing answers'
                    />
                    <span>{t(prefixedT('RESULT_SCREEN.MISSING'))}</span>
                    <span>{simulationData?.stats?.missing || 0}</span>
                  </div>
                  {simulationData?.stats?.not_counted ? (
                    <div className='summary_result ms-4 '>
                      <img
                        className='mx-auto'
                        src={icon_not_counted}
                        alt='Not counted'
                      />
                      <span>
                        {t(prefixedT('RESULT_SCREEN.NOT_COUNTED_ABBR'))}
                      </span>
                      <span>{simulationData?.stats?.not_counted || 0}</span>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              ) : (
                <div className=''>
                  <p className='remaining-answer-text'>
                    {}
                    {`${t(prefixedT('RESULT_SCREEN.REMAINING_ANSWER'))} ${
                      (simulationData?.questions?.length || 0) -
                      (simulationData?.stats?.correct || 0)
                    }:`}
                  </p>
                  <div className='d-flex justify-content-between mt-3'>
                    <div className='d-flex'>
                      <img
                        src={icon_correct_not_shure}
                        className='me-1 icon_result icon-24px'
                        alt='Correct but unsure'
                      />
                      <span className='text-2'>
                        {t(prefixedT('RESULT_SCREEN.CORRECT_UNSURE'))}
                      </span>
                    </div>
                    <div className='d-flex'>
                      <strong className='ms-1 text-3'>
                        {simulationData?.stats?.uncertain_correct || 0}
                      </strong>
                    </div>
                  </div>
                  <div className='d-flex justify-content-between mt-3'>
                    <div className='d-flex'>
                      <img
                        src={icon_wrong_not_shure}
                        className='me-1 icon_result icon-24px'
                        alt='Wrong but unsure'
                      />
                      <span className='text-2'>
                        {t(prefixedT('RESULT_SCREEN.WRONG_UNSURE'))}
                      </span>
                    </div>
                    <div className='d-flex'>
                      <strong className='ms-1 text-3'>
                        {simulationData?.stats?.uncertain_wrong || 0}
                      </strong>
                    </div>
                  </div>
                  <div className='d-flex justify-content-between mt-3'>
                    <div className='d-flex'>
                      <img
                        src={icon_wrong_answer}
                        className='me-1 icon_result'
                        alt='Wrong answers'
                      />
                      <span className='text-2'>
                        {t(prefixedT('RESULT_SCREEN.WRONG'))}
                      </span>
                    </div>
                    <div className='d-flex'>
                      <strong className='ms-1 text-3'>
                        {(simulationData?.stats?.wrong || 0) -
                          (simulationData?.stats?.uncertain_wrong || 0)}
                      </strong>
                    </div>
                  </div>
                  <div className='d-flex justify-content-between mt-3'>
                    <div className='d-flex'>
                      <img
                        src={icon_not_counted}
                        className='me-1 icon_result'
                        alt='Missing answers'
                      />
                      <span className='text-2'>
                        {t(prefixedT('RESULT_SCREEN.MISSING'))}
                      </span>
                    </div>
                    <div className='d-flex'>
                      <strong className='ms-1 text-3'>
                        {simulationData?.stats?.missing || 0}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
              {simulationData.type !== 'incorrect_questions' && (
                <span
                  onClick={() => setShowDetailedResults(true)}
                  className='modal_anchor pointer mt-3'
                >
                  {t(prefixedT('RESULT_SCREEN.DETAILS'))}
                </span>
              )}
            </div>
          </>
        )}

        {simulationData?.is_official_structure &&
          simulationData?.admission_test?.previous_blocking_scores?.[0] && (
            <div
              className={
                (simulationData?.admission_test?.previous_blocking_scores?.[0]
                  ?.score || 0) > (simulationData?.stats?.total_score || 0)
                  ? 'card test_not_pass'
                  : 'card test_pass'
              }
            >
              <div className='result_label'>
                <img src={icon_degree} alt='Degree comparison' />
                <span>
                  {t(prefixedT('RESULT_SCREEN.LAST_YEAR_COMPARISON'))}
                </span>
              </div>

              {(simulationData?.admission_test?.previous_blocking_scores?.[0]
                ?.score || 0) > (simulationData?.stats?.total_score || 0) ? (
                <div className='summary_result'>
                  <img
                    className='mx-auto'
                    src={icon_not_pass}
                    alt='Not in range'
                  />
                  <span className='label_small'>
                    {t(prefixedT('RESULT_SCREEN.IN_THIS_CASE'))}
                  </span>
                  <span className='big_result_text'>
                    {t(prefixedT('RESULT_SCREEN.NOT_IN_RANGE'))}
                  </span>
                </div>
              ) : (
                <div className='summary_result'>
                  <img className='mx-auto' src={icon_pass} alt='In range' />
                  <span className='label_small'>
                    {t(prefixedT('RESULT_SCREEN.IN_THIS_CASE'))}
                  </span>
                  <span className='big_result_text'>
                    {t(prefixedT('RESULT_SCREEN.IN_RANGE'))}
                  </span>
                </div>
              )}

              <span
                onClick={() => {
                  setShowPassModal(true);
                }}
                className='modal_anchor pointer mt-5'
              >
                {t(prefixedT('RESULT_SCREEN.DETAILS'))}
              </span>
              <DetailedPassModal
                show={showPassModal}
                user_score={simulationData?.stats?.total_score || 0}
                previous_score={
                  simulationData?.admission_test?.previous_blocking_scores?.[0]
                }
                handleClose={() => {
                  setShowPassModal(false);
                }}
                isBocconiDesign={simulationData?.is_bocconi_design}
              />
            </div>
          )}
      </div>

      {/* Bottom navigation buttons */}
      <div className='mt-5 mx-auto navigation-container'>
        {(simulationData?.type !== 'comparative' ||
          simulationData?.show_review) && (
          <button
            id='closeModalButton'
            className='w-100'
            onClick={() => {
              navigate('/revision', { replace: true });
            }}
          >
            <img src={icon_bulb} className='me-2' alt='Review questions' />
            {t(prefixedT('RESULT_SCREEN.REVIEW_QUESTIONS'))}
          </button>
        )}

        {simulationData?.type !== 'incorrect_questions' &&
          !isModuleBasedTresholds && (
            <div className='d-flex navigation_buttons'>
              <button
                id='closeModalButton'
                disabled={true}
                className='mt-2 white w-100'
              >
                {t(prefixedT('BUTTONS.BACK'))}
              </button>
              <button
                id='closeModalButton'
                onClick={() => {
                  setStep(1);
                }}
                className='mt-2 ms-2 white w-100'
              >
                {t(prefixedT('BUTTONS.AHEAD'))}
              </button>
            </div>
          )}
      </div>

      <DetailedResultsModal
        data={simulationData?.stats}
        show={showDetailedResults}
        handleClose={() => {
          setShowDetailedResults(false);
        }}
        isBocconiDesign={simulationData?.is_bocconi_design}
      />

      {/* DetailedResultsModal for each module  */}
      {selectedDiscipline && (
        <DetailedResultsModal
          data={selectedDiscipline}
          show={showDisciplineModal}
          handleClose={() => {
            setShowDisciplineModal(false);
            setSelectedDiscipline(null);
          }}
          isBocconiDesign={simulationData?.is_bocconi_design}
        />
      )}
    </>
  );
}
